import { StrictMode, useMemo, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Provider as ReduxProvider } from 'react-redux';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { ResponsiveToastContainer } from '@components/ui/ResponsiveToastContainer';

import App from './App';
import { store } from '@store/index';
import { buildMuiTheme } from '@config/theme';
import { useColorMode } from '@hooks/useColorMode';
import { applyInitialColorMode, mountThemeVariables } from '@config/themeBootstrap';

import 'react-toastify/dist/ReactToastify.css';
import './index.css';

// Materialize design tokens + apply the resolved color mode BEFORE first paint
// so the splash itself respects the chosen / persisted theme (no light flash).
mountThemeVariables();
applyInitialColorMode();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('Root element #root not found');

/**
 * Rebuilds the MUI theme whenever the color mode flips so MUI's palette (and
 * its derived light/dark shades + dark-mode defaults) tracks the active theme.
 * The Tailwind side adapts independently via the `.dark` class on <html>.
 */
function ThemedApp({ children }: { children: ReactNode }) {
  const { mode } = useColorMode();
  const theme = useMemo(() => buildMuiTheme(mode), [mode]);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

createRoot(root).render(
  <StrictMode>
    <ReduxProvider store={store}>
      <QueryClientProvider client={queryClient}>
        <ThemedApp>
          <BrowserRouter>
            <App />
          </BrowserRouter>
          <ResponsiveToastContainer />
        </ThemedApp>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ReduxProvider>
  </StrictMode>,
);
